import { BROADCAST_CHANNEL } from '@/broadcastChannel/broadcast-channel.const';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { usePubsub } from '@/composables/generics';
import credentials from '@/credentials';
import { useAlertsActions, useAccountGetters } from '@/store-composables';
import { useAuthActions, useAuthGetters } from '@/store-composables/auth';
import { STATUS_CODES } from '@/utils/error-handling-service';
import FeatureToggleService from '@/utils/feature-toggle-service';
import pusherService from '@/utils/pusher';

const initUser = async () => {
	const { getAlertsStats, startPollingAlerts } = useAlertsActions();
	const { subscribeToRefreshToken, subscribeToInvalidToken } = await usePubsub();
	const { getUser, saveCredentials } = useAuthActions();
	if (credentials.user && credentials.token) {
		subscribeToRefreshToken();
		subscribeToInvalidToken();
		try {
			await getUser(credentials.user);
			const { user } = useAuthGetters();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const userValue: any = user.value; // cuz User isn't typed yet
			if (credentials.account || userValue?.profile?.account) {
				const ftsUser = FeatureToggleService.buildFtsUserObject(
					userValue.id,
					userValue.email,
					userValue.first_name,
					userValue.last_name,
					userValue.custom_fields
				);
				FeatureToggleService.initializeUser(ftsUser);
				saveCredentials({ creds: credentials });
				startPollingAlerts();
				getAlertsStats();
				pusherService.setPusherForUser(credentials.token, credentials.account, credentials.user);
			}
		} catch (err) {
			if (
				err instanceof Error &&
				'response' in err &&
				typeof err.response === 'object' &&
				err.response !== null &&
				'status' in err.response &&
				typeof err.response.status === 'number' &&
				[STATUS_CODES.UNAUTHORIZED, STATUS_CODES.NOT_FOUND].includes(err.response.status)
			) {
				await clearUser();
			} else {
				throw err;
			}
		}
	} else {
		await clearUser();
	}
};

const clearUser = async () => {
	const { unsubscribeToInvalidToken, unsubscribeToRefreshToken } = await usePubsub();
	unsubscribeToRefreshToken();
	unsubscribeToInvalidToken();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onPostLogin = async (params: Record<string, any>) => {
	const { postLogin } = useAuthActions();
	await postLogin({
		token: params.token,
		user_id: params.user,
		refresh: params.refresh,
		[BROADCAST_CHANNEL]: true,
		active_account_id: params.account,
	});
	const { account } = useAccountGetters();
	if (account.value) {
		AppDialogManagerService.closeAllDialogs();
		AppDialogManagerService.openWorkspaceChangedDialog({ account: account.value });
	}
};

export const useAuth = () => ({ initUser, clearUser, onPostLogin });
